<template>
    <div class="d-flex flex-column">
        <img id="source">
        <!-- Slides -->
        <vueper-slides ref="galleryPicker" class="mb-1"  @slide="onSlideChanged" :style="{paddingBottom: `${heightPercentage}% !important`}" :touchable="false" :arrows="false" :bullets="false" fractions>
            <!-- Images -->
            <vueper-slide class="position-relative" v-for="(image, index) in images" :key="index">
                <template v-slot:content>
                    <div title="Choose an image" class="base-image-input col-12 col-lg-6 p-0" :style="{ 'background-image': `url(${getImageUrl(image)})`, width: '100%', paddingBottom: `${heightPercentage}% !important`, height: '0 !important' }"></div>     
                    <button class="d-flex justify-content-center align-items-center delete-button" @click.stop="remove(index)"><CIcon name="cilMinus"></CIcon></button>
                </template>     
            </vueper-slide>
            <!-- Placeholder -->
            <vueper-slide v-if="!images.length">
                <template v-slot:content>
                    <image-uploader :id="id" ref="fileInput" outputFormat="blob" :maxWidth="maxWidth" :maxHeight="maxHeight" :maxSize="maxSize" :quality="0.9" :preview="false" accept=".png,.jpg,.jpeg" @input="onFileSelect" className="native-file-input">
                        <label :for="id" slot="upload-label" class="m-0">
                            <div title="Choose an image" class="cursor-pointer base-image-input col-12 col-lg-6 p-0" :style="{ width: '100%', paddingBottom: `${heightPercentage}% !important`, height: '0 !important' }">
                                <span class="d-flex flex-column j placeholder position-absolute">
                                    {{ $t('chooseImage') }}
                                    <pre class="m-0 text-muted">{{readableRatio}}</pre>
                                    <pre class="m-0 text-muted">.png/.jpg/.jpeg</pre>
                                </span>   
                            </div>     
                        </label>
                    </image-uploader>     
                </template>
            </vueper-slide>
        </vueper-slides>
        <!-- Controls -->
        <div class="gallery-controls mt-3">
            <!-- Add Image Button - Always visible when under max limit -->
            <div class="add-image-section mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="image-counter">
                        {{ images.length }}{{ max > 0 ? `/${max}` : '' }} {{ $t('experiences.images') }}
                    </span>
                </div>
                <image-uploader v-if="max == 0 || images.length < max" :id="id" ref="fileInput" outputFormat="blob" :maxWidth="maxWidth" :maxHeight="maxHeight" :maxSize="maxSize" :quality="0.9" :preview="false" accept=".png,.jpg,.jpeg" @input="onFileSelect" className="native-file-input">
                    <label :for="id" slot="upload-label" class="add-image-button">
                        <CIcon name="cilPlus" class="mr-2"></CIcon>
                        {{ $t('addImage') }}
                    </label>
                </image-uploader>
                <div v-else class="max-images-reached text-muted text-center">
                    {{ $t('maxImagesReached') }} ({{ max }})
                </div>
            </div>

            <!-- Navigation Controls - Only show when there are images -->
            <div v-if="images.length > 0" class="navigation-controls d-flex justify-content-between align-items-center">
                <!-- Previous -->
                <button 
                    v-bind:class="{ disabled: images.length <= 1 }" 
                    class="nav-button" 
                    @click="$refs.galleryPicker.previous()"
                    :disabled="images.length <= 1"
                >
                    <CIcon name="cilArrowLeft"></CIcon>
                </button>
                
                <!-- Reorder Controls -->
                <div v-if="allowReorder && images.length > 1" class="reorder-controls d-flex gap-2">
                    <button 
                        v-bind:class="{ disabled: images.length <= 1 }" 
                        class="reorder-button" 
                        @click="reorderImages(-1)"
                        :disabled="images.length <= 1"
                        :title="$t('moveLeft')"
                    >
                        <CIcon name="cilArrowThickFromRight"></CIcon>
                    </button>
                    <button 
                        v-bind:class="{ disabled: images.length <= 1 }" 
                        class="reorder-button" 
                        @click="reorderImages(1)"
                        :disabled="images.length <= 1"
                        :title="$t('moveRight')"
                    >
                        <CIcon name="cilArrowThickFromLeft"></CIcon>
                    </button>
                </div>
                
                <!-- Next -->
                <button 
                    v-bind:class="{ disabled: images.length <= 1 }" 
                    class="nav-button" 
                    @click="$refs.galleryPicker.next()"
                    :disabled="images.length <= 1"
                >
                    <CIcon name="cilArrowRight"></CIcon>
                </button>
            </div>
        </div>
        <!-- Cropper -->
        <BasePopUp v-if="showModal" v-model="showModal" @detailsClosed="showModal = false">
            <VueCropper v-show="selectedFile" ref="cropper" :src="selectedFile" alt="Source Image" @ready="onCropStart" :aspectRatio="applyRatio" :initialAspectRatio="applyRatio"></VueCropper>
            <CRow class="justify-content-between m-0 p-1">
                <CButton v-show="selectedFile" color="secondary" text @click="showModal = false">{{$t('cancel')}}</CButton>
                <!-- <div class="d-flex align-items-center">
                    <input type="checkbox" id="checkbox" v-model="waterMarkCheckBox">
                    <label for="checkbox" class="m-0 px-1">{{watermark}}</label>
                </div> -->
                <CButton v-show="selectedFile" color="primary" @click="confirmClicked">{{$t('crop')}}</CButton>
            </CRow>
        </BasePopUp>
    </div>
</template>

<script>
import BasePopUp from "@/controls/popups/BasePopUp.vue";
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { getReducedFraction } from "@/helpers/utils.js";
import { VueperSlides, VueperSlide } from 'vueperslides'
import 'vueperslides/dist/vueperslides.css'
import ImageUploader from 'vue-image-upload-resize'
import mergeImages from 'merge-images';
import { API_HELPER } from "@/helpers/api.js";

const MAX_SIZE = 2; // MB

export default {
    components: { VueCropper, BasePopUp, VueperSlides, VueperSlide, ImageUploader },
        props: {
        value: null, // MAX 1 means object, others is array
        ratioWidth: {
            type: Number,
            default: 16
        },
        ratioHeight: {
            type: Number,
            default: 9
        },
        max: {
            type: Number,
            default: 0 // Infinite
        },
        maxSize:{
            type: Number,
            default: 2 * 1024 * 1024 // 2MB in bytes
        },
        maxWidth:{
            type: Number,
            default: 1920
        },
        maxHeight:{
            type: Number,
            default: 1080
        },
        allowReorder:{
            type: Boolean,
            default: false
        },
        watermark:
        {
            type: String,
            default: "Watermark"
        }
    },
    data() {
        return {
            images: this.value ? (Array.isArray(this.value) ? this.value : [this.value]) : [],
            mimeType: '',
            fileName: '',
            selectedFile: '',
            showModal: false,
            currentSlide: 0,
            id: "imageUploader" + new Date().valueOf(), // the ID for the file input, required if more than one instance should be used on the same page.
            waterMarkCheckBox: false,
            exportAlwaysJPEG: "image/jpeg"
        }
    },
    computed:
    {
        applyRatio: function()
        {
            return this.ratioWidth / this.ratioHeight;
        },
        readableRatio: function()
        {
            return getReducedFraction(this.ratioWidth, this.ratioHeight);
        },
        heightPercentage: function()
        {
            return this.ratioHeight / this.ratioWidth * 100;
        },
        halfHeightPercentage: function()
        {
            return this.heightPercentage / 2;
        }
    },
    created() {
        window.addEventListener("resize", this.onWindowResize);
    },
    destroyed() {
        window.removeEventListener("resize", this.onWindowResize);
    },
    methods: {
        getImageUrl(image) {
            // Handle different image data structures
            if (!image) return '';
            
            // If image has a url property, use it
            if (image.url) {
                return image.url;
            }
            
            // If image is a string (path from API), construct the full URL
            if (typeof image === 'string') {
                return API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, image]);
            }
            
            // If image has a data property (base64), use it
            if (image.data) {
                return `data:${image.mime || 'image/jpeg'};base64,${image.data}`;
            }
            
            return '';
        },
        // Builds the payload sent to the API from the data URL produced by the canvas.
        // The mime is read from the data URL itself (not from this.mimeType) because it is the
        // format the canvas actually produced — PNGs are re-encoded to JPEG in confirmClicked.
        buildImagePayload(dataUrl)
        {
            const match = /^data:([^;,]+)[;,]/.exec(dataUrl);
            const mime = match ? match[1] : 'image/jpeg';
            const data = dataUrl.split(',')[1];
            // The server always generates a new name (GUID): `name` is only used to validate the
            // extension. An empty extension on an unexpected format makes the API return 400
            // instead of storing bytes under the wrong extension.
            const extension = mime === 'image/png' ? '.png' : (mime === 'image/jpeg' ? '.jpg' : '');
            // `data` feeds the local preview (getImageUrl) and the handler guards; `base64` is the
            // field LocalIOService reads. No `url`: getImageUrl rebuilds the data URL from `data`,
            // which keeps the request from carrying the file three times over.
            return { name: `image${extension}`, mime: mime, data: data, base64: data };
        },
        onSlideChanged(currentSlide)
        {
            this.currentSlide = currentSlide;
            this.$emit('currentSlideChanged', currentSlide.currentSlide.index);
        },
        remove(index)
        {
            // Get current slide index before removal
            const currentSlideIndex = this.currentSlide && this.currentSlide.currentSlide ? this.currentSlide.currentSlide.index : 0;
            
            // Remove the image at the specified index
            this.images.splice(index, 1);
            
            // Adjust slide after removal
            if (this.images.length > 0) {
                let newSlideIndex;
                
                // If removing the current slide or one after it
                if (index >= currentSlideIndex) {
                    // If removing current slide, go to previous (or stay at 0)
                    // If removing slide after current, stay on current
                    newSlideIndex = index === currentSlideIndex ? Math.max(0, currentSlideIndex - 1) : currentSlideIndex;
                } else {
                    // If removing a slide before current, adjust index
                    newSlideIndex = currentSlideIndex - 1;
                }
                
                // Ensure index is within bounds
                newSlideIndex = Math.min(newSlideIndex, this.images.length - 1);
                
                // Navigate to the correct slide
                this.$nextTick(() => {
                    if (this.$refs.galleryPicker) {
                        this.$refs.galleryPicker.goToSlide(newSlideIndex);
                    }
                });
            }
        },
        onChooseImageClicked()
        {
            this.$refs.fileInput.click();
        },
        // Note that validation of size and extension is guaranteed by the vue-image-upload-resize plugin
        onFileSelect(blob) 
        {
            try
            {
                // Get file information
                const inputDOM = document.getElementById(this.id);
                const file = inputDOM.files[0];
                if(file.type === "image/png") this.mimeType = this.exportAlwaysJPEG
                else this.mimeType = file.type;
                this.fileName = file.name;
                const maxSize = 1024 * 1024 * MAX_SIZE;

                // Validations
                if(file.size > maxSize)
                {
                    throw this.$t('fileErrorMessage');
                }

                // Load cropper
                const reader = new FileReader()
                reader.onload = (event) => {
                    this.showModal = true;
                    this.selectedFile = event.target.result;
                    this.$nextTick(() => {
                        this.$refs.cropper.replace(this.selectedFile);
                    });
                }
                reader.readAsDataURL(blob);
            }
            catch(err)
            {
                alert(err);
            }
        },
        confirmClicked() {
            // Get the cropped canvas and set the format to JPEG
            let croppedImage = this.$refs.cropper.getCroppedCanvas({ 
                maxWidth: this.maxWidth, 
                maxHeight: this.maxHeight, 
                fillColor: 'white' // Fill transparent areas with white if necessary
            });

            let width = croppedImage.width;
            let height = croppedImage.height;
            let imageData = croppedImage.toDataURL(this.mimeType);
            let images = [imageData];
            
            if(this.waterMarkCheckBox) {
                // PLUGIN https://github.com/csbun/resize-image
                let resizeImage = require('resize-image');
                let image = new Image();
                image.src = "/img/watermark.png";
                image.onload = () => {
                    let data = resizeImage.resize(image, width, height);
                    images.push(data)
                    mergeImages(images, {format: this.mimeType, width: width, height: height })
                    .then(base64Image =>{
                        let newImage = this.buildImagePayload(base64Image);
                        this.images.push(newImage);
                        this.$refs.galleryPicker.goToSlide(this.images.length - 1);
                    });
                }
            }else{
                let newImage = this.buildImagePayload(imageData);
                this.images.push(newImage);
                this.$refs.galleryPicker.goToSlide(this.images.length - 1);
            }

            this.showModal = false;
        },
        setupCanvasAndCrop()
        {
            const cropper = this.$refs.cropper;
 
            if(cropper)
            {
                const image = cropper.getImageData();
                const imageHeight = image.height;
                const imageWidth = image.width;

                const container = document.querySelector(".modal-body > div");
                const containerHeight = container.clientHeight;
                const containerWidth = container.clientWidth;

                if(imageHeight > imageWidth)
                {
                    if(imageHeight > containerHeight)
                    {
                        const height = containerHeight;
                        const width = imageWidth * height / imageHeight;
                        const left = containerWidth / 2 - width / 2;
                        const top = 0;

                        cropper.setCanvasData({ left: left, top:top, width: width, height: height });
                        cropper.setCropBoxData({ left: left, top:top, height: height });
                    }
                }
                // // This logic doesn't seem to be necessary
                // else
                // {
                //     if(imageWidth > containerWidth)
                //     {

                //     }
                // }
            }
        },
        onWindowResize()
        {
            this.setupCanvasAndCrop();
        },
        onCropStart()
        {
            this.setupCanvasAndCrop();
        },
        reorderImages(shift)
        {
            const fromIndex = this.currentSlide.currentSlide.index;
            
            let toIndex = fromIndex + shift;
            if(toIndex < 0) toIndex = this.images.length - 1;
            else if(toIndex > this.images.length - 1) toIndex = 0;

            const element = this.images.splice(fromIndex, 1)[0];
            this.images.splice(toIndex, 0, element);

            this.$refs.galleryPicker.goToSlide(toIndex);
        }
    },
    watch:
    {
        value: function(val)
        {
            this.images = val ? (Array.isArray(val) ? val : [val]) : [];
        },
        showModal: function(val)
        {
            if(!val)
            {
                // this.$refs.fileInput.value=null;
                this.$refs.cropper.reset();
                this.setupCropBox = false;
                this.waterMarkCheckBox = false;
            }
        },
        images: function(val) 
        { 
            // If max is 1, emit only the first image as a single object, otherwise emit the full array
            if (this.max === 1 && val && val.length > 0) {
                this.$emit('input', val[0]);
            } else {
                this.$emit('input', val);
            }
        }
    }
}
</script>

<style scoped>
.delete-button
{
    position: absolute;
    top: 0;
    right: 0;
}

.is-invalid .base-image-input .placeholder
{
    background-color: #e55353;
}

.base-image-input {
  display: block;
  background-size: cover;
  background-position: center center;
  max-width: unset !important;
}

.placeholder {
  background: #F0F0F0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #333;
  font-size: 18px;
  font-family: Helvetica, Arial, Verdana, Tahoma, sans-serif;
}

.placeholder:hover {
  background: #E0E0E0;
}

button
{
    border-color: #d8dbe0;
    border-top-color: rgb(216, 219, 224);
    border-right-color: rgb(216, 219, 224);
    border-bottom-color: rgb(216, 219, 224);
    border-left-color: rgb(216, 219, 224);
    border-radius: 0.25rem;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
}

/deep/.vueperslides
{
    min-width: 250px;
}

/deep/.vueperslides__inner {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
}

/deep/.vueperslides__parallax-wrapper {
    height: 100%;
}

/deep/.vueperslides__parallax-wrapper
{
    padding-bottom: 0 !important;
}

/deep/.cropper-container {
    max-height: 80vh;
}

/deep/.cropper-bg
{
    width: 100% !important;
}

/deep/.native-file-input
{
    display: none;
}

/deep/.native-file-input ~ label
{
    width: 100%;
    height: 100%;
}

/* New Gallery Controls Styles */
.gallery-controls {
    border-top: 1px solid #dee2e6;
    padding-top: 1rem;
}

.add-image-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 1.5rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
    text-decoration: none;
    min-width: 140px;
}

.add-image-button:hover {
    background-color: #0056b3;
    color: white;
    text-decoration: none;
}

.add-image-button:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.max-images-reached {
    padding: 0.75rem;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    font-size: 0.875rem;
}

.navigation-controls {
    gap: 0.5rem;
}

.nav-button, .reorder-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid #d8dbe0;
    background-color: #fff;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.nav-button:hover:not(.disabled), .reorder-button:hover:not(.disabled) {
    background-color: #f8f9fa;
    border-color: #adb5bd;
}

.nav-button.disabled, .reorder-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f8f9fa;
}

.reorder-controls {
    gap: 0.25rem;
}

.reorder-button {
    width: 35px;
    height: 35px;
    font-size: 0.875rem;
}

.image-counter {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6c757d;
}

</style>